import deepEqual from "fast-deep-equal"

import type { ValueOf } from "@p-ban/common/src/types"
import { shallowDiff } from "@p-ban/common/src/utils/ObjectUtils"

import getDisplayCondition from "../../../service/getDisplayCondition"
import getInitialValue from "../../../service/getInitialValue"
import type { Touched } from "../../../types"
import type EstimateValues from "../../../types/params"
import visibleValidator from "../visibleValidator/visibleValidator"
import replaceValues from "./replaceValues/replaceValues"

/**
 * 表示されてない選択肢が選択されてる場合に、初期値に選択状態を切り替えます。
 */
const convertToDisplayedValues = (currentValues: EstimateValues) => {
  const displayedValues: EstimateValues = { ...currentValues }
  const isDisplayed = getDisplayCondition(currentValues)

  const entries = Object.entries(visibleValidator) as [
    keyof typeof visibleValidator,
    ValueOf<typeof visibleValidator>
  ][]

  for (const [field, isSelectedOptionDisplayed] of entries) {
    // 項目が表示されているが、選択されている選択肢が非表示の時
    if (isDisplayed[field] && !isSelectedOptionDisplayed(currentValues, currentValues[field])) {
      displayedValues[field] = getInitialValue[field](currentValues)
    }
  }

  return displayedValues
}

const convertToInitialValueIfNoTouched = (currentValues: EstimateValues, touched: Touched) => {
  const nextValues: EstimateValues = { ...currentValues }
  const isDisplayed = getDisplayCondition(currentValues)

  for (const field of Object.keys(currentValues) as (keyof EstimateValues)[]) {
    if (isDisplayed[field] && !touched[field]) {
      // 型の解決が難しいので仕方なくanyにして代入してます。
      ;(nextValues as any)[field] = getInitialValue[field](currentValues)
    }
  }

  return nextValues
}

/**
 * フォームの状態の更新処理
 */
const updateValuesInternal = (
  /**
   * フォームの入力内容
   */
  values: EstimateValues,
  /**
   * ユーザーが各項目への変更操作を行ったかどうかのフラグ
   */
  touched: Touched,
  /**
   * 一連の更新処理で更新されたフォームのフィールドの集合
   */
  updatedFields: Set<keyof EstimateValues>,
  /**
   * 再帰処理がされた回数
   */
  count: number
): { values: EstimateValues; touched: Touched } => {
  // 表示されている選択肢の名前が一緒で内部的なコード値が違う選択肢が存在します。
  // それらの選択状態を維持するため、同一の名前の違うコード値への置き換えをします。
  const replacedValues = replaceValues(values)

  // ユーザーが変更操作を行ってない項目を初期値に置き換えます。
  const changedValues = convertToInitialValueIfNoTouched(replacedValues, touched)

  // 一連の更新処理で現在選択している選択肢が非表示になる場合があります。
  // その場合に非表示になった項目について、初期値を選択状態に変更します。
  const nextValues = convertToDisplayedValues(changedValues)

  // 後続のtouchedの更新のため変更があったフィールドの記録をします。
  // ユーザーによる変更操作の記録をリセットするために使うので
  // 選択肢の名前が変わらない場合は変更されたとみなしません、
  // そのため values ではなく replacedValues との比較をします。
  const diffs = shallowDiff(replacedValues, nextValues)
  Object.entries(diffs).forEach(([name, diff]) => {
    if (diff !== undefined) {
      updatedFields.add(name as keyof EstimateValues)
    }
  })

  // 現在のフォームの状態と更新されたフォームの状態に差がある場合は、
  // 更新された選択肢により更に他の選択肢が変更になる場合があります。
  // そのため更新前後のフォームの状態に差分がある場合は、
  // 再帰処理をして再度、更新処理をおこないます。
  // フォームがDate型の値を持っているためdeepEqualで比較をしています。
  if (!deepEqual(values, nextValues)) {
    // 再帰処理のプログラムにバグがあった場合に無限ループとならないように
    // 一定回数を超えたら例外を投げて強制終了させます。
    if (count > 100) {
      throw new Error("再帰回数が上限を超えました。")
    }

    return updateValuesInternal(nextValues, touched, updatedFields, count + 1)
  }

  const nextTouched = { ...touched }
  // システムから変更した値はtouchedの状態をリセットします。
  for (const field of updatedFields.values()) {
    nextTouched[field] = false
  }

  return { values: nextValues, touched: nextTouched }
}

const updateValues = (values: EstimateValues, touched: Touched): { values: EstimateValues; touched: Touched } => {
  return updateValuesInternal(values, touched, new Set<keyof EstimateValues>(), 0)
}

export default updateValues
